import { Component, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import { SidebarComponent } from './components/sidebar/sidebar.component';
import { ConnectionBannerComponent } from './components/connection-banner/connection-banner.component';
import { SyncScheduler } from './core/offline/sync.scheduler';
import { PerfilService } from './core/perfil/perfil.service';
import { ActualizacionDeLaAppService } from './core/pwa/actualizacion-de-la-app.service';
import { AvisoDeActualizacionComponent } from './core/pwa/aviso-de-actualizacion.component';
import { SessionService } from './core/tenant/session.service';
import { AvisoDelDemoComponent } from './core/demo/aviso-del-demo.component';
import { AyudaComponent } from './ayuda/ayuda.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, ConnectionBannerComponent, AvisoDeActualizacionComponent, AvisoDelDemoComponent, AyudaComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class App {
  private router = inject(Router);
  private syncScheduler = inject(SyncScheduler);
  // T1 (2026-09-08): el perfil vertical se pide una vez por sesión y pinta el
  // POS con el color de su vertical. Se instancia aquí, al arrancar, para que
  // el `effect` que sigue a la sesión exista desde el primer render.
  private perfil = inject(PerfilService);
  // F2.7: la pestaña abierta recibe la versión nueva sin forzar el service worker.
  private actualizacion = inject(ActualizacionDeLaAppService);
  private session = inject(SessionService);

  /** Oculta el chrome (sidebar) en rutas de pantalla completa (login/registro). */
  readonly showChrome = signal(true);
  /**
   * Modo Ruta se usa en el celular: desplaza la página entera, no una caja de 100vh por dentro. Con la caja, la barra del
   * navegador no se recogía y el dedo peleaba con el rebote de la página («se siente pegado», Santiago 26/10).
   */
  readonly enDocumento = signal(false);

  constructor() {
    // F2: arranca el drenado del outbox de órdenes en segundo plano (idempotente).
    this.syncScheduler.start();
    this.actualizacion.iniciar();
    // Al entrar con una cuenta (o cambiar de negocio) se mira si hay versión nueva.
    let negocioAnterior: string | undefined;
    effect(() => {
      const negocio = this.session.session()?.tenantId;
      if (negocioAnterior !== undefined && negocio !== negocioAnterior) {
        this.actualizacion.alIniciarSesion();
      }
      negocioAnterior = negocio;
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const url = e.urlAfterRedirects;
        // F6.3: el Modo Ruta pinta su propio marco (cabecera y pestañas abajo); la barra lateral de la caja no cabe
        // en un teléfono y taparía las pestañas.
        this.showChrome.set(
          !url.startsWith('/login') && !url.startsWith('/register') && !url.startsWith('/reset') && !url.startsWith('/bienvenida') &&
            !url.startsWith('/ruta') && !url.startsWith('/bodega'),
        );
        this.enDocumento.set(url.startsWith('/ruta'));
      });
  }
}
